import { useState, useEffect } from 'react';
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Card, CardContent, IconButton } from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useAuth } from '../../AuthContext';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import EventIcon from '@mui/icons-material/Event';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const Leave = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelLeaveId, setCancelLeaveId] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: ''
  });
  const [loading, setLoading] = useState(true);

  const statusText = {
    0: 'Pending',
    1: 'Inprogress',
    2: 'Approved',
    3: 'Rejected',
    4: 'Returned'
  };

  const statusColor = {
    0: colors.orange[100],
    1: colors.green[100],
    2: colors.blue[100],
    3: colors.red[100],
    4: colors.blue[100],
  };

  useEffect(() => {
    async function fetchLeaves() {
      try {
        const response = await getData('proccess.php', 'leaves_V2', userId);
        if (response.success) {
          setLeaves(response.data);
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch leaves");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchLeaves();
  }, [userId, getData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await addData('proccess.php', 'leaves_V2', {
        staff_id: userId,
        leave_type: formData.leave_type,
        reason: formData.reason,
        startDate: formData.startDate,
        endDate: formData.endDate,
        back_in_office: formData.back_in_office,
        department: formData.department,
        status: '0'
      });
      if (response.success) {
        toast.success("Leave request submitted successfully");
        setLeaves([...leaves, { ...response.data, id: response.data.id }]);
        setOpenLeaveDialog(false);
        setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
      } else {
        toast.error(response.message || "Failed to submit leave request");
      }
    } catch (err) {
      toast.error("Network error: Failed to submit leave request");
      console.error("Leave submit error:", err);
    }
  };

  const handleEditLeave = (leave) => {
    setEditingLeave(leave);
    setFormData({
      leave_type: leave.leave_type,
      reason: leave.reason,
      startDate: leave.startDate,
      endDate: leave.endDate,
      back_in_office: leave.back_in_office,
      department: leave.department
    });
    setOpenLeaveDialog(true);
  };

  const handleUpdateLeave = async (e) => {
    e.preventDefault();
    if (!editingLeave) return;
    try {
      const response = await updateData('proccess.php', 'leaves_V2', {
        id: editingLeave.id,
        leave_type: formData.leave_type,
        reason: formData.reason,
        startDate: formData.startDate,
        endDate: formData.endDate,
        back_in_office: formData.back_in_office,
        department: formData.department,
        status: editingLeave.status
      },editingLeave.id);
      if (response.success) {
        toast.success("Leave request updated successfully");
        setLeaves(leaves.map(l => l.id === editingLeave.id ? { ...l, ...formData } : l));
        setOpenLeaveDialog(false);
        setEditingLeave(null);
        setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
      } else {
        toast.error(response.message || "Failed to update leave request");
      }
    } catch (err) {
      toast.error("Network error: Failed to update leave request");
      console.error("Leave update error:", err);
    }
  };

  const handleCancelLeave = async () => {
    if (!cancelLeaveId) return;
    try {
      const response = await deleteData('proccess.php', 'leaves_V2', cancelLeaveId);
      if (response.success) {
        toast.success("Leave request canceled successfully");
        setLeaves(leaves.filter(l => l.id !== cancelLeaveId));
        setOpenCancelDialog(false);
        setCancelLeaveId(null);
      } else {
        toast.error(response.message || "Failed to cancel leave request");
      }
    } catch (err) {
      toast.error("Network error: Failed to cancel leave request");
      console.error("Leave cancel error:", err);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 50 },
    { field: "leave_type", headerName: "Leave Type", flex: 1, minWidth: 120 },
    { field: "startDate", headerName: "Start Date", flex: 1, minWidth: 120 },
    { field: "endDate", headerName: "End Date", flex: 1, minWidth: 120 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 120,
      renderCell: (params) => (
        <Typography>{statusText[params.row.status] || 'Unknown'}</Typography>
      )
    }
  ];

  const recentLeaves = leaves.filter(leave => leave.status >= 4);
  const currentLeaves = leaves.filter(leave => leave.status < 4);

  return (
    <Box mt={2}>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Box width="100%" display="flex" justifyContent="end">
            <Button
              variant="contained"
              startIcon={<EventIcon />}
              onClick={() => {
                setEditingLeave(null);
                setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
                setOpenLeaveDialog(true);
              }}
              sx={{ mb: 2, backgroundColor: colors.blue[400], '&:hover': { backgroundColor: colors.blue[500] } }}
            >
              Apply for Leave
            </Button>
          </Box>

          <Box display="flex" justifyContent="space-between" flexWrap="wrap-reverse">
            <Box paddingBottom="1rem" flex="1" maxWidth="1000px" minWidth="350px" m="0.3rem">
              <Typography variant="h5" fontWeight={700}>Recent Leaves</Typography>
              <Box paddingTop="1rem">
                {recentLeaves.length > 0 ? (
                  recentLeaves.map(leave => (
                    <Box key={leave.id}>
                      <Card sx={{ backgroundColor: statusColor[leave.status], color: 'black', mb: '1rem' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="h5" fontWeight={700}>
                              {leave.leave_type} Leave
                            </Typography>
                            <Typography variant="h4" fontWeight={700}>
                              {statusText[leave.status]}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 0 0">
                            <Typography>
                              {leave.startDate} - {leave.endDate}
                            </Typography>
                            <Typography variant="h5" fontWeight={700} color="orangered">
                              Back In Office: {leave.back_in_office}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 1rem 0">
                            <Typography>
                              Approved/Rejected Date: {leave.supervisor_appr_date || leave.rejected_date || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography>{leave.comments || 'No comments'}</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))
                ) : (
                  <Typography>No Recent Leaves</Typography>
                )}
              </Box>
            </Box>

            <Box flex="1" maxWidth="700px" minWidth="350px" m="0.3rem">
              <Typography variant="h5" fontWeight={700}>Current Leave Request</Typography>
              <Box paddingTop="1rem">
                {currentLeaves.length > 0 ? (
                  currentLeaves.map(leave => (
                    <Box key={leave.id}>
                      <Card sx={{ backgroundColor: statusColor[leave.status], color: 'black', mb: '1rem' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h5" fontWeight={700}>
                              {leave.leave_type} Leave
                            </Typography>
                            <Box>
                              {leave.status < 2 && (
                                <>
                                  <IconButton
                                    onClick={() => handleEditLeave(leave)}
                                    sx={{ color: colors.blue[400] }}
                                    title="Edit Leave"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => {
                                      setCancelLeaveId(leave.id);
                                      setOpenCancelDialog(true);
                                    }}
                                    sx={{ color: colors.red[400] }}
                                    title="Cancel Leave"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </>
                              )}
                              <Typography variant="h4" fontWeight={700}>
                                {statusText[leave.status]}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 0 0">
                            <Typography>
                              {leave.startDate} - {leave.endDate}
                            </Typography>
                            <Typography variant="h5" fontWeight={700} color="orangered">
                              Back In Office: {leave.back_in_office}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 1rem 0">
                            <Typography>
                              Approved/Rejected Date: {leave.supervisor_appr_date || leave.rejected_date || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography>{leave.comments || 'No comments'}</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))
                ) : (
                  <Typography>No Current Leave Requests</Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Dialog
            open={openLeaveDialog}
            onClose={() => {
              setOpenLeaveDialog(false);
              setEditingLeave(null);
              setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{editingLeave ? 'Edit Leave' : 'Apply for Leave'}</DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={editingLeave ? handleUpdateLeave : handleLeaveSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <TextField
                  select
                  label="Leave Type"
                  name="leave_type"
                  value={formData.leave_type}
                  onChange={handleFormChange}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="Annual">Annual</option>
                  <option value="Sick">Sick</option>
                  <option value="Compassionate">Compassionate</option>
                  <option value="Study">Study</option>
                  <option value="Maternity">Maternity</option>
                </TextField>
                <TextField
                  label="Reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  required
                />
                <TextField
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label="Back in Office"
                  name="back_in_office"
                  type="date"
                  value={formData.back_in_office}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  select
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleFormChange}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="ICT">ICT</option>
                  <option value="SCIENCE">SCIENCE</option>
                  <option value="ART">ART</option>
                  <option value="HR">HR</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="STEAM">STEAM</option>
                  <option value="ENGINEERING">ENGINEERING</option>
                </TextField>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenLeaveDialog(false);
                  setEditingLeave(null);
                  setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={editingLeave ? handleUpdateLeave : handleLeaveSubmit}
                variant="contained"
                sx={{ backgroundColor: colors.blue[400] }}
                endIcon={<SendIcon />}
              >
                {editingLeave ? 'Update' : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openCancelDialog}
            onClose={() => {
              setOpenCancelDialog(false);
              setCancelLeaveId(null);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Confirm Cancellation</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to cancel this leave request?</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenCancelDialog(false);
                  setCancelLeaveId(null);
                }}
              >
                No
              </Button>
              <Button
                onClick={handleCancelLeave}
                variant="contained"
                sx={{ backgroundColor: colors.red[400] }}
              >
                Yes, Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Leave;